interface DuplicationOptions {
  minLength?: number
  maxLength?: number
  preserveOrder?: boolean
}

/**
 * Advanced array duplication with options
 * @template T - The type of array elements
 * @param array - The source array to duplicate
 * @param options - Configuration options
 * @returns A new array with duplicated content
 */
export const duplicateArray = <T>(
  array: readonly T[],
  options: DuplicationOptions = {}
): T[] => {
  const {
    minLength = 10,
    maxLength = Infinity,
    preserveOrder = true
  } = options

  if (minLength < 0 || maxLength < 0) {
    throw new Error('Length values must be non-negative')
  }

  if (minLength > maxLength) {
    throw new Error('minLength cannot be greater than maxLength')
  }

  if (array.length === 0) {
    return []
  }

  if (array.length >= minLength) {
    return [...array].slice(0, maxLength)
  }

  const result: T[] = [...array]

  while (result.length < minLength && result.length < maxLength) {
    const remainingNeeded = Math.min(minLength - result.length, maxLength - result.length)
    const itemsToAdd = Math.min(remainingNeeded, array.length)

    if (preserveOrder) {
      result.push(...array.slice(0, itemsToAdd))
    } else {
      // Could add shuffling logic here if needed
      result.push(...array.slice(0, itemsToAdd))
    }
  }

  return result.slice(0, maxLength)
}
